import React from 'react';
import { connect } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import ViewTitle from '../../../common/layout/ViewTitle';
import InfoButton from '../../../common/data_display/InfoButton';
import { formatDateTime } from '../../../common/data_display/utils';
import ReportNotFound from '../../misc/ReportNotFound';
import BackendError from '../../misc/BackendError';
import PredictionError from '../../misc/PredictionError';
import ReportRunning from '../../misc/ReportRunning';
import { PredictionStatus } from "../consts";
import AdmCumulativeChart from './AdmCumulativeChart';
import AdmBacklogChart from './AdmBacklogChart';
import AdmDefectTable from './AdmDefectTable';
import AdmDeliveryTable from './AdmDeliveryTable';
import ExecutiveInfoModal from '../executive_view/ExecutiveInfoModal';

const axios = require('axios').default;

function AdmExecutiveView(props) {
  const { release, jwtToken, baseApiUri, getUrl } = props;

  // -------------------------------- State -------------------------------- //
  // ----------------------------------------------------------------------- //
  const [loadCount, setLoadCount] = React.useState(0)

  const [report, setReport] = React.useState();
  const [status, setStatus] = React.useState();
  const [timestamp, setTimestamp] = React.useState();
  const [backendError, setBackendError] = React.useState(false);
  const [reportNotFound, setReportNotFound] = React.useState(false);

  const [modalOpen, setModalOpen] = React.useState(false);

  // --------------------------- Lifecycle Hooks --------------------------- //
  // ----------------------------------------------------------------------- //
  React.useEffect(() => {
    if (release) {
      setReport(undefined);
      setBackendError(false);
      setReportNotFound(false);

      const headers = { Authorization: jwtToken };

      axios.get(`${baseApiUri}${getUrl(release)}`, { headers })
        .then(response => {
          setReport(response.data);
          setStatus(response.data.status);
          setTimestamp(response.data.timestamp);

          if (response.data.status?.toUpperCase() === PredictionStatus.RUNNING.toUpperCase()) {
            setTimeout(() => setLoadCount(loadCount + 1), 2000);
          }
        })
        .catch(err => {
          if (err.response) {
            if (err.response.status === 404) {
              setReportNotFound(true);
            }
            else if (err.response.status >= 500) {
              setBackendError(true);
            }
          } else {
            // Network error or other issue
            setBackendError(true);
          }
        })
    }
  }, [release, getUrl, jwtToken, baseApiUri, loadCount]);


  // --------------------------- Event Handlers ---------------------------- //
  // ----------------------------------------------------------------------- //
  const handleModalOpen = () => setModalOpen(true);
  const handleModalClose = () => setModalOpen(false);

  // ------------------------------- Render -------------------------------- //
  // ----------------------------------------------------------------------- //
  if (backendError) return <BackendError />
  if (reportNotFound) return <ReportNotFound />

  if (status?.toUpperCase() === PredictionStatus.RUNNING.toUpperCase()) return <ReportRunning timestamp={timestamp} />

  const title = `ADM Executive Summary ${timestamp ? `(${formatDateTime(timestamp)})` : ""}`

  // Check if we have the data structure we need
  const hasValidData = report && report.raw_response && report.raw_response.arrival && report.raw_response.closure;

  return (
    <React.Fragment>
      <ViewTitle title={title} prediction_type={report ? report.prediction_type : undefined}>
        <InfoButton onClick={handleModalOpen} />
      </ViewTitle>

      {status?.toUpperCase() === PredictionStatus.ERROR.toUpperCase() && (
        <PredictionError error={report?.error} />
      )}

      {!hasValidData && report && status?.toUpperCase() !== PredictionStatus.ERROR.toUpperCase() && status?.toUpperCase() !== PredictionStatus.RUNNING.toUpperCase() && (
        <PredictionError error="Invalid or incomplete ADM data structure received from the server." />
      )}

      {
        hasValidData && (
          <Grid container spacing={2} justifyContent='center'>
            <Grid item xs={12} lg={6}>
              <AdmDeliveryTable {...report} />
            </Grid>
            <Grid item xs={12} lg={6}>
              <AdmDefectTable {...report} />
            </Grid>
            <Grid item xs={12} md={6}>
              <AdmCumulativeChart {...report.raw_response} increment={report.increment} d0_interval={report.raw_response.d0_interval} d2_interval={report.raw_response.d2_interval} />
            </Grid>
            <Grid item xs={12} md={6}>
              <AdmBacklogChart {...report.raw_response} increment={report.increment} d0_interval={report.raw_response.d0_interval} d2_interval={report.raw_response.d2_interval} />
            </Grid>
          </Grid>
        )
      }

      <ExecutiveInfoModal open={modalOpen} onClose={handleModalClose} />
    </React.Fragment>
  )
}

function mapStateToProps(state) {
  return {
    release: state.common.release,
    jwtToken: state.common.jwtToken,
    baseApiUri: state.common.baseApiUri,
  };
}

export default connect(mapStateToProps)(AdmExecutiveView)
